/**
 * ticket-type-row — one ticket type as a selectable row: two lines on the
 * left, and a fixed-width right-hand slot holding a stepper, a locked qty
 * readout, a status label, or nothing at all — the SAME geometry across every
 * slot, with the qty numeral at the same x whether the stepper flanks it or
 * not, so a list's own rows never shift shape and its one figure never slides
 * sideways as their meaning changes.
 *
 * Line one is the type's name and, beside it, the door into everything said
 * about that type. Line two is the price, left-aligned under the name, with
 * the live hints beside it. The description is on neither line: the row prints
 * the two facts a purchase is made of and nothing else, and what an operator
 * wrote is read behind the door, at reading scale, whole. A row therefore
 * cannot be lengthened, shortened, or reflowed by anything the operator types
 * — the only thing a description changes about a row is whether its door is
 * live.
 *
 * Which is why the door's box is reserved on EVERY row, described or not: the
 * icon renders unconditionally and goes `invisible` where there is nothing to
 * open, exactly as the stepper's − does at qty 0 and for the same reason the
 * `reserved` slot kind states on the other side of the row. The box is the
 * point. An affordance that appears only where a description exists makes
 * described rows taller than their neighbours, so a list of five types would
 * render at two heights depending on which of them somebody happened to
 * describe. `visibility: hidden` also drops the hidden icon from the tab
 * order, the accessibility tree and hit-testing alike, so a reserved box is
 * inert in every sense but the geometric one. Measured: 70px per row at 358px,
 * 390px and 640px, described and bare alike — line one 32px from the icon's
 * own box, line two 20px from the price, plus the row's 16px of padding.
 *
 * The descent is two steps. The name is title/md on full ink and leads the
 * row; the price is title/sm on full ink, one rung under it. Both are facts of
 * the purchase, which is what full ink says and what keeps the price out of
 * the register everything said ABOUT the purchase is written in — but reading
 * order already ranks them now that they are stacked, so an equal rung would
 * state that rank twice and render the row as two headings rather than a name
 * answered by its price. The hints ride the price's own line at body/sm on
 * onVariant, exactly one step under it: they are all facts about what this
 * costs, so they belong beside the cost, and one step is what makes the price
 * the line's subject and the hints its qualifiers. `tabular-nums` keeps a
 * column of prices aligned down the list.
 *
 * Both lines take `truncate` — `text-overflow: ellipsis`, which is a paint
 * decision the browser makes from the box it already has, so nothing here
 * measures anything and no verdict can go stale. A name cut by the column says
 * so and has nowhere to open; the hints, cut behind a price that never yields,
 * say the same. The full name still reaches a screen reader whatever the
 * column does, because `text-overflow` shortens what the eye reads and never
 * touches the accessible text.
 *
 * The DOOR is TWO controls for one overlay, on every row that carries a
 * description and at every width: the type's name as a real `<button>` and an
 * `info` icon-button immediately beside it. The glyph is what says a door is
 * here — always rendered, so the advertisement is a shape rather than a mark
 * on the text, and it reads at rest and under a finger without asking a
 * contrast ratio to carry it. Both open the same `ticket-type-description` — both facts at reading
 * scale, the full text with the author's own newlines intact, and a stepper
 * that keeps buying reachable while the buyer reads. Real buttons and not ARIA
 * over a div, so role, tab stop, Enter/Space and the accessible name all come
 * from the platform. They are keyed on whether a description EXISTS and never
 * on whether a line happened to overflow: an affordance keyed on a measurement
 * is present on a phone and gone on a laptop, which reads as the list being
 * inconsistent rather than as the text being long.
 *
 * They carry different accessible names because they are two doors a screen
 * reader announces one after the other, and two controls reading identically
 * is a choice with no way to make it. Each says the thing only it offers: the
 * name — which the column may have cut — offers itself in full, and the icon
 * offers more information. The name's own mark is a dotted hairline at a 4px
 * offset that rests transparent and returns at `outline` on hover and on
 * focus-visible, so a list of these rows reads as text at rest and the mark is
 * there at a full 3:1 the moment a pointer arrives. `underline` stays on the
 * element with only its colour taken off, rather than the utility removed,
 * because a `decoration-*` colour paints nothing without a decoration line to
 * colour: the line is what the hover state has left to work on. No resting
 * underline survives anywhere in the product now — the legal and consent prose
 * links that once wore this same dotted geometry carry `text-primary-default
 * hover:underline` instead, so the whole system marks a link on approach and
 * not at rest. Discovery here
 * is the glyph's job precisely because a REVEALED mark cannot do it on a touch
 * screen, where there is no hover to reveal anything, and the focus ring the
 * platform paints on this button — not the decoration — is what answers for
 * the keyboard. The glyph is
 * `size="xs"` — 16px, the smallest rung the icon scale carries and well under
 * the stepper's 40px pair, so it is the quietest glyph in the row — and
 * `variant="text"`, so no border of its own competes with the outlined pair on
 * the far side. The name button
 * hugs its own text rather than filling the column, so the pixels whose
 * meaning changed are exactly its own and every other pixel of the
 * row — the price, the padding, the whole right side — still increments on
 * tap. Both doors wear `cursor-help` against the row's own `cursor-pointer`,
 * because the cost of guessing wrong is asymmetric: miss the door and you buy
 * a ticket.
 *
 * The description stays offered in every row state — sold out, outside its
 * sales window, or basketLocked-readonly — since it describes the type itself,
 * not its current purchasability. The overlay is mounted detached by `show()`,
 * so its own +/− cannot bubble here and a `row` captured at open would not
 * follow the basket: this row relays the overlay's gestures as its own
 * `increment`/`decrement` and pushes each newly-resolved `row` into the live
 * instance, so the numeral the buyer taps and the numeral in the list behind
 * it are one value and the caller wires nothing new.
 *
 * Purely presentational: every field on `row` is resolved by the caller
 * before this element ever sees it — price formatting, discount/scarcity/
 * sale-window hints, and the readonly-lock decision are each a different
 * checkout's own business rule, so this component carries none of them.
 * type-selector.view.ts (the storefront's live checkout) and the guest-issue
 * add/edit flows both render the identical row for that reason: the same
 * tap-to-increment shape, wired to two entirely different backing models.
 *
 * Tap-to-increment and the +/− buttons dispatch the same `increment`/
 * `decrement` CustomEvents; the caller owns the qty write. Body-click stays
 * live even where the stepper carries a typed field or the name carries a
 * door — the field, the +/− buttons and both doors each stop propagation on
 * their own click/keydown, so a gesture that actually lands on them never
 * reaches the row underneath, and reading a description never buys a ticket.
 * What DOES give way in either case is the row's `role="button"` +
 * `tabindex="0"` + Enter/Space: that role is children-presentational and would
 * prune the nested control out of the accessibility tree entirely, so a row
 * offering one drops its own button semantics and leaves the nested control
 * and the +/− buttons independently focusable and announced as themselves. A
 * row with nothing to describe nests nothing — its reserved icon is
 * `visibility: hidden` and therefore not in the tree at all — so it keeps the
 * role, and the two conditions read the same static field.
 *
 * A stepper whose caller supplies `qtyField` swaps that numeral for a
 * `<schmancy-typography-field>` carrying the SAME type scale — headline/lg,
 * centered, tabular — so it reads as the numeral it replaces rather than a
 * boxed input dropped into a list row, and dispatches the typed text as
 * `qtyEntered` on commit. Text, not a number: what "" or "-3" or "abc" means
 * is the caller's rule, the same way the qty write already is. The value binds
 * through `live()` because the caller's clamp routinely maps distinct text to
 * the qty already committed, and lit skips a binding whose value did not
 * change — leaving the raw text on screen over an order that reads otherwise.
 * The typed cell is w-14 where the plain numeral is w-12, absorbing the slack
 * the slot box already carried so three digits fit at 32px without the box —
 * and therefore the row's silhouette — changing; past three it overflows
 * rather than wraps, because a row that grows a line while someone types in it
 * is the one defect the slot's fixed geometry exists to prevent. That slack is
 * also why the slot is 144px: the typed variant fills 140 of it — 40px − plus
 * the 56px cell plus 40px + plus the two 2px gaps — so the slot has none to
 * lend the name's column. A blank field IS qty 0: the placeholder prints the
 * same dimmed numeral the stepper would, so
 * landing on an unselected row and typing replaces nothing.
 *
 * The field is reachable by pointer and by programmatic focus, but NOT by Tab
 * inside a schmancy overlay: that surface drives Tab from its own
 * `focusableDescendants()` list, whose selector a bare contenteditable matches
 * nothing in, and giving the host a tabindex instead only makes the trap lose
 * its place, since focus delegates inward to an element the list never holds.
 * Closing that needs the field's own editable region to be focusable — a
 * change inside the library, not one a caller can declare.
 */
// The row's other tags ride whatever registered them upstream; this one is
// named because it is the only tag here a caller can opt into, and a barrel
// this file does not name is a blank qty cell rather than a build error.
import "@mhmo91/schmancy/form/fields/typography";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type PropertyValues, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { finalize, fromEvent, merge, Subject, takeUntil, tap } from "rxjs";
import { TicketTypeDescriptionView } from "./ticket-type-description.view.ts";

export type TicketTypeRowHint = {
	readonly text: string;
	readonly tone?: "warning" | "error";
};

export type TicketTypeRowSlot =
	| {
			readonly kind: "stepper";
			readonly qty: number;
			readonly canIncrement: boolean;
			readonly canDecrement: boolean;
			/** Caller-supplied, same reason as `rowAriaLabel` — each checkout
			 *  localizes (or doesn't) on its own. */
			readonly incrementAriaLabel: string;
			readonly decrementAriaLabel: string;
			/** Non-null turns the qty numeral into the typable field described in
			 *  the header docstring, named by `ariaLabel`; `null` leaves it the
			 *  plain readout. An opt-in ON the stepper rather than a fifth slot
			 *  kind: a typable numeral is the same slot — same qty, same +/−,
			 *  same box — differing only in whether its middle cell accepts
			 *  keystrokes, and a parallel kind would restate all five stepper
			 *  fields and let the two drift apart on the next one added. */
			readonly qtyField: { readonly ariaLabel: string } | null;
	  }
	| { readonly kind: "lockedQty"; readonly qty: number }
	| {
			readonly kind: "status";
			readonly label: string;
			readonly tone: "error" | "muted";
	  }
	/** Held open with nothing in it — for a row whose right-hand side has
	 *  nothing true to say. The box is the whole point: the row keeps its
	 *  place among siblings that still carry a numeral or a label, and a
	 *  quantity of zero is never printed as if it were a fact worth reading. */
	| { readonly kind: "reserved" };

export type TicketTypeRow = {
	readonly id: string;
	readonly name: string;
	readonly description: string | null;
	readonly priceLabel: string;
	readonly hints: readonly TicketTypeRowHint[];
	readonly selected: boolean;
	readonly receded: boolean;
	readonly clickable: boolean;
	/** Caller-supplied — each checkout has its own i18n register (or none),
	 *  so this component names no copy of its own. `null` renders no
	 *  aria-label override. */
	readonly rowAriaLabel: string | null;
	/** The accessible name of the button the type's NAME becomes wherever a
	 *  description exists. It names what only that door offers — the name
	 *  itself, which the column is free to cut, read in full — because the icon
	 *  beside it is a second door onto the same overlay and a screen reader
	 *  announces the two one after the other. Read only where `description` is
	 *  non-null. */
	readonly nameButtonAriaLabel: string;
	/** The accessible name of the `info` icon-button beside the name. A glyph
	 *  carries no meaning to a screen reader, so this string is the whole of
	 *  what that door announces, and it says the thing only it offers: more
	 *  information. Read only where `description` is non-null — the reserved
	 *  box on every other row is `visibility: hidden` and reaches no
	 *  accessibility tree. */
	readonly descriptionButtonAriaLabel: string;
	/** The label on the description overlay's own Back button. Caller-supplied
	 *  for the same reason every other string here is: this component names no
	 *  copy, and the overlay it opens reads its copy off this same row rather
	 *  than resolving a second register of its own. */
	readonly descriptionBackLabel: string;
	readonly slot: TicketTypeRowSlot;
};

@schmancyElement("ticket-type-row")
export class TicketTypeRowView extends SchmancyElement {
	@property({ type: Object }) row!: TicketTypeRow;

	/** The open overlay, while one is open. `show()` mounts it detached, so it
	 *  is unreachable from the template and there is nothing for lit to keep
	 *  current — the handle is what lets `updated` re-feed it. */
	private descriptionOverlay: TicketTypeDescriptionView | null = null;

	/** A class field and not an inline arrow: both doors bind it by reference,
	 *  so each keeps one identity across the re-renders every caller triggers on
	 *  every qty write, and the two are demonstrably the same gesture rather
	 *  than two handlers that happen to agree today. */
	private readonly openDescription = (e: Event): void => {
		// The row body acts on a click; reading a description must never buy.
		e.stopPropagation();
		const overlay = new TicketTypeDescriptionView();
		overlay.row = this.row;
		this.descriptionOverlay = overlay;
		// Mounted detached, so the overlay's own +/− reach nothing by bubbling:
		// they are listened for on the instance and leave again as this row's
		// own events, which is why the caller needs no second qty-write wiring.
		// `dismissed$` is what ends those two legs — the overlay's stream
		// completes on dismissal, and a `fromEvent` never completes on its own.
		const dismissed$ = new Subject<void>();
		merge(
			fromEvent(overlay, "increment").pipe(tap(() => this.increment())),
			fromEvent(overlay, "decrement").pipe(tap(() => this.decrement())),
			show(overlay, { ariaLabel: this.row.name }).pipe(
				finalize(() => {
					this.descriptionOverlay = null;
					dismissed$.next();
				}),
			),
		)
			.pipe(takeUntil(merge(this.disconnecting, dismissed$)))
			.subscribe();
	};

	protected override updated(changed: PropertyValues): void {
		super.updated(changed);
		// The open overlay reads the same row this row does, so the numeral in
		// its footer is the basket's own and never the snapshot taken at open —
		// the caller rebuilds the row on every qty write, and this is the only
		// path that reaches an element lit does not own.
		if (this.descriptionOverlay !== null)
			this.descriptionOverlay.row = this.row;
	}

	private increment(): void {
		this.dispatchEvent(
			new CustomEvent("increment", { bubbles: true, composed: true }),
		);
	}

	private decrement(): void {
		this.dispatchEvent(
			new CustomEvent("decrement", { bubbles: true, composed: true }),
		);
	}

	override render(): TemplateResult {
		const r = this.row;
		const slot = r.slot;
		// The click affordance and the button-role semantics are independent:
		// a click on the body carries no ARIA pruning, so it stays live even
		// where a typed field sits in the stepper or the name carries a door
		// (each stops propagation on its own gestures). `role="button"` DOES
		// prune — it is children-presentational — so it gives way whenever the
		// row nests a control of its own, dropping the row's own Enter/Space
		// handling and leaving that control and the +/− buttons independently
		// reachable. Both conditions read the same static fields the door and
		// the field do, so the role and the control it would prune appear and
		// vanish together and no layout measurement reaches either.
		const clickIncrements = r.clickable;
		const announceAsButton =
			r.clickable &&
			r.description === null &&
			!(slot.kind === "stepper" && slot.qtyField !== null);

		// One template read twice so the name at rest and the name as a door
		// cannot drift: whichever renders, it is the same typography at the same
		// rung with the same truncation.
		const name = html`
			<schmancy-typography
				type="title"
				token="md"
				class="min-w-0 truncate tracking-tight"
				>${r.name}</schmancy-typography
			>
		`;

		return html`
			<schmancy-surface
				type="containerLow"
				role=${ifDefined(announceAsButton ? "button" : undefined)}
				tabindex=${ifDefined(announceAsButton ? "0" : undefined)}
				aria-label=${ifDefined(
					announceAsButton ? (r.rowAriaLabel ?? undefined) : undefined,
				)}
				class=${this.classMap({
					"flex items-center gap-1 rounded-xl px-2 py-2": true,
					"opacity-60": r.receded,
					// The hover wash is the same token-driven surface tint
					// schmancy-list-item already uses for its own hover state —
					// a fill, not a border — so hovering an unselected row never
					// reads as the selected ring below.
					"cursor-pointer hover:bg-[color-mix(in_srgb,var(--schmancy-sys-color-surface-on)_8%,transparent)]":
						clickIncrements,
					"ring-1 ring-primary-default": r.selected,
				})}
				@click=${() => {
					if (clickIncrements) this.increment();
				}}
				@keydown=${(e: KeyboardEvent) => {
					if (!announceAsButton || (e.key !== "Enter" && e.key !== " ")) return;
					e.preventDefault();
					this.increment();
				}}
			>
				<div class="flex min-w-0 flex-1 flex-col gap-0.5">
					<div class="flex min-w-0 items-center gap-1">
						${
							r.description === null
								? name
								: html`
									<button
										type="button"
										aria-haspopup="dialog"
										class="min-w-0 cursor-help text-left underline decoration-dotted decoration-transparent underline-offset-4 hover:decoration-outline focus-visible:decoration-outline"
										aria-label=${r.nameButtonAriaLabel}
										@click=${this.openDescription}
										@keydown=${(e: KeyboardEvent) => {
											// The row body acts on exactly these two; the button's own
											// activation already handles them, so letting them travel
											// would buy a ticket every time someone opens a description.
											if (e.key === "Enter" || e.key === " ")
												e.stopPropagation();
										}}
									>${name}</button>
								`
						}
						<!-- Rendered on every row and hidden where there is nothing to
						     open, so a described row and a bare one measure the same:
						     visibility:hidden holds the box while dropping the button
						     from the tab order, the accessibility tree and hit-testing,
						     which is also what lets the row keep its own button role
						     wherever no description exists. -->
						<schmancy-icon-button
							icon="info"
							size="xs"
							variant="text"
							color="neutral"
							class=${this.classMap({
								"shrink-0 cursor-help": true,
								invisible: r.description === null,
							})}
							aria-haspopup="dialog"
							aria-label=${r.descriptionButtonAriaLabel}
							@click=${this.openDescription}
							@keydown=${(e: KeyboardEvent) => {
								if (e.key === "Enter" || e.key === " ") e.stopPropagation();
							}}
						></schmancy-icon-button>
					</div>
					<!-- The price and everything true about what it costs, on one
					     line: the price never yields — shrink-0 against hints free to
					     take what is left — because a cut figure is a lie where a cut
					     hint is honest about being cut. -->
					<div class="flex min-w-0 items-baseline gap-2">
						<schmancy-typography
							type="title"
							token="sm"
							class="shrink-0 tabular-nums"
							>${r.priceLabel}</schmancy-typography
						>
						<schmancy-typography
							type="body"
							token="sm"
							class="min-w-0 truncate text-surface-onVariant"
							>${repeat(
								r.hints,
								(_, i) => i,
								(h, i) =>
									html`${i === 0 ? "" : " · "}<span class=${h.tone === "warning" ? "text-warning-default" : h.tone === "error" ? "text-error-default" : ""}>${h.text}</span>`,
							)}</schmancy-typography
						>
					</div>
				</div>

				<!-- One reserved slot, one geometry for every kind. The locked
				     numeral keeps the stepper's own middle cell: the spacers flanking
				     it stand exactly where the −/+ buttons do — 40px each side — so
				     losing the buttons never slides the one glyph the buyer is
				     watching. − and + are the same 40px box, the row's own tap-target
				     floor. At qty 0 there is nothing to remove, so − goes "invisible"
				     rather than absent — the box holds the numeral's x, and
				     visibility:hidden drops the button from the tab order, the
				     accessibility tree, and hit-testing alike. That last one is why −
				     owns a click-swallowing cell covering its whole box: the row body
				     increments, so a miss on − once it is hidden would buy a ticket.
				     + needs no cell; a miss there increments, which is what the hand
				     was reaching for. -->
				<div class="flex min-h-10 w-36 shrink-0 items-center justify-end gap-0.5">
					${
						slot.kind === "lockedQty"
							? html`
								<div class="w-10 shrink-0"></div>
								<schmancy-typography type="headline" token="lg" class=${this.classMap({ "w-12 text-center tabular-nums": true, "text-primary-default": r.selected })}>
									${slot.qty}
								</schmancy-typography>
								<div class="w-10 shrink-0"></div>
							`
							: slot.kind === "stepper"
								? html`
									<div
										class="flex w-10 shrink-0 items-center justify-center self-stretch"
										@click=${(e: Event) => e.stopPropagation()}
									>
										<schmancy-icon-button
											icon="remove"
											size="sm"
											variant="outlined"
											color="neutral"
											class=${this.classMap({ invisible: slot.qty === 0 })}
											?disabled=${!slot.canDecrement}
											aria-label=${slot.decrementAriaLabel}
											@click=${(e: Event) => {
												e.stopPropagation();
												this.decrement();
											}}
										></schmancy-icon-button>
									</div>
									${when(
										slot.qtyField,
										(field) => html`
											<schmancy-typography-field
												type="headline"
												token="lg"
												align="center"
												placeholder="0"
																	label=${field.ariaLabel}
												class=${this.classMap({ "w-14 shrink-0 tabular-nums whitespace-nowrap": true, "text-primary-default": r.selected })}
												.value=${live(slot.qty > 0 ? String(slot.qty) : "")}
												@click=${(e: Event) => {
													e.stopPropagation();
												}}
												@keydown=${(e: KeyboardEvent) => {
													// Only the two keys the row body itself acts on. Escape has to
													// keep reaching the document listener that dismisses the overlay
													// this row usually sits inside, so a blanket stop would trap a
													// typing operator in the dialog.
													if (e.key === "Enter" || e.key === " ")
														e.stopPropagation();
												}}
												@change=${(e: CustomEvent<{ value: string }>) => {
													e.stopPropagation();
													this.dispatchEvent(
														new CustomEvent("qtyEntered", {
															detail: { value: e.detail.value },
															bubbles: true,
															composed: true,
														}),
													);
												}}
											></schmancy-typography-field>
										`,
										() => html`
											<schmancy-typography type="headline" token="lg" class=${this.classMap({ "w-12 text-center tabular-nums": true, "text-primary-default": r.selected })}>
												${slot.qty}
											</schmancy-typography>
										`,
									)}
									<schmancy-icon-button
										icon="add"
										size="sm"
										variant="outlined"
										color="neutral"
										?disabled=${!slot.canIncrement}
										aria-label=${slot.incrementAriaLabel}
										@click=${(e: Event) => {
											e.stopPropagation();
											this.increment();
										}}
									></schmancy-icon-button>
								`
								: slot.kind === "status"
									? html`
										<schmancy-typography
											type="label"
											token="lg"
											class=${slot.tone === "error" ? "text-error-default" : "text-surface-onVariant"}
										>
											${slot.label}
										</schmancy-typography>
									`
									: html``
					}
				</div>
			</schmancy-surface>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-type-row": TicketTypeRowView;
	}
}
